import { useCallback, useEffect, useRef, useState } from "react";

import { setSoremonMood, type SoremonMood } from "./soremonStore";
import { useMessengerScopeSubscribe } from "../../app/messenger/MessengerSocketContext";
import type { SoremonProgressPayload } from "../../types/messengerEvents";

/* Completed は短い発光だけ、Error は静かにしばらく残す（体験仕様「9. 発光」）。 */
const COMPLETED_MOOD_MS = 1800;
const ERROR_MOOD_MS = 5000;

interface SoremonActivityInput {
  available: boolean;
  open: boolean;
  /** 送信・実行・キャンセル・リセット・アップロードのいずれかの応答待ち */
  waiting: boolean;
  uploading: boolean;
  hasPendingAction: boolean;
}

/**
 * soremon の今の状態（体験仕様「15. 基本状態」）を決め、ストアに書き込む
 * （ヘッダーのキャラクターにも同じ状態が映る）。
 *
 * 優先順: 応答待ち（Thinking / Acting）→ Error → Asking（確認待ちの操作あり）→
 * Completed → Listening（パネルを開いている）→ Idle。
 * Thinking / Acting は BE の途中経過（WebSocket の soremon.progress）で切り替える。
 * Completed / Error は `showFlash` を呼んだあと一定時間だけ出す。
 */
export const useSoremonActivity = ({
  available,
  open,
  waiting,
  uploading,
  hasPendingAction,
}: SoremonActivityInput) => {
  const [progress, setProgress] = useState<SoremonProgressPayload | null>(null);
  useMessengerScopeSubscribe(available ? "soremon" : undefined, (ev) => {
    if (ev.type === "soremon.progress") setProgress(ev.payload);
  });
  useEffect(() => {
    if (!waiting) setProgress(null);
  }, [waiting]);

  const [flash, setFlash] = useState<"completed" | "error" | null>(null);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showFlash = useCallback((kind: "completed" | "error") => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    setFlash(kind);
    flashTimerRef.current = setTimeout(
      () => setFlash(null),
      kind === "error" ? ERROR_MOOD_MS : COMPLETED_MOOD_MS,
    );
  }, []);
  useEffect(() => () => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
  }, []);

  const progressMood: SoremonMood = uploading ? "acting" : progress?.state ?? "thinking";
  const progressLabel = uploading ? "ファイルをアップロード" : progress?.label ?? "";

  let mood: SoremonMood;
  if (waiting) mood = progressMood;
  else if (flash === "error") mood = "error";
  else if (hasPendingAction) mood = "asking";
  else if (flash === "completed") mood = "completed";
  else mood = open ? "listening" : "idle";

  useEffect(() => {
    setSoremonMood(available ? mood : "idle");
  }, [available, mood]);

  return { mood, progressMood, progressLabel, showFlash };
};
